import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeft, TrendingUp, Calendar, Shield, DollarSign, Clock, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Skeleton } from '../../components/ui/Skeleton';
import { useInvestmentProduct, useInvestmentSimulation, useApplyInvestment, useValidateSuitability, useInvestorProfile } from '../../hooks/useInvestments';

const riskConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'default'; color: string }> = {
  VERY_LOW: { label: 'Muito Baixo', variant: 'success', color: 'text-green-400' },
  LOW: { label: 'Baixo', variant: 'success', color: 'text-green-400' },
  MEDIUM: { label: 'Médio', variant: 'warning', color: 'text-yellow-400' },
  HIGH: { label: 'Alto', variant: 'error', color: 'text-orange-400' },
  VERY_HIGH: { label: 'Muito Alto', variant: 'error', color: 'text-red-400' },
};

const typeLabels: Record<string, string> = {
  CDB: 'CDB',
  LCI: 'LCI',
  LCA: 'LCA',
  FUND: 'Fundo',
  DEBENTURE: 'Debênture',
  CRI: 'CRI',
  CRA: 'CRA',
};

const rateTypeLabels: Record<string, string> = {
  FIXED: 'Taxa Fixa',
  CDI_PERCENTAGE: '% do CDI',
  CDI_PLUS: 'CDI +',
  IPCA_PLUS: 'IPCA +',
};

const liquidityLabels: Record<string, string> = {
  DAILY: 'Liquidez Diária',
  AT_MATURITY: 'No Vencimento',
  GRACE_PERIOD: 'Após Carência',
};

export function InvestmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [term, setTerm] = useState('365');
  const [showApplyModal, setShowApplyModal] = useState(false);

  const { data: product, isLoading } = useInvestmentProduct(id || '');
  const { data: profile } = useInvestorProfile();
  const { data: suitability } = useValidateSuitability(id || '');
  const simulationMutation = useInvestmentSimulation();
  const applyMutation = useApplyInvestment();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatRate = (product: any) => {
    if (product.rateType === 'FIXED') {
      return `${(product.rate * 100).toFixed(2)}% a.a.`;
    }
    if (product.rateType === 'CDI_PERCENTAGE') {
      return `${(product.rate * 100).toFixed(0)}% do CDI`;
    }
    if (product.rateType === 'CDI_PLUS') {
      return `CDI + ${(product.rate * 100).toFixed(2)}%`;
    }
    if (product.rateType === 'IPCA_PLUS') {
      return `IPCA + ${(product.rate * 100).toFixed(2)}%`;
    }
    return `${(product.rate * 100).toFixed(2)}%`;
  };

  const handleSimulate = () => {
    if (!id || !amount) return;
    simulationMutation.mutate({
      productId: id,
      amount: Number(amount) * 100,
      term: Number(term),
    });
  };

  const handleApply = async () => {
    if (!id || !amount) return;
    try {
      await applyMutation.mutateAsync({
        productId: id,
        amount: Number(amount) * 100,
      });
      setShowApplyModal(false);
      navigate('/banking/investments/portfolio');
    } catch (error) {
      console.error('Erro ao aplicar:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="w-12 h-12 text-surface-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-100 mb-2">Produto não encontrado</h2>
        <Button variant="secondary" onClick={() => navigate('/banking/investments')}>
          Voltar
        </Button>
      </div>
    );
  }

  const risk = riskConfig[product.riskRating] || { label: product.riskRating, variant: 'default' as const, color: 'text-surface-400' };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" size="sm" onClick={() => navigate('/banking/investments')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">{product.name}</h1>
          <p className="text-surface-400">{product.issuer}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Product Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Informações do Produto
                </span>
                <div className="flex gap-2">
                  <Badge variant="primary">{typeLabels[product.type] || product.type}</Badge>
                  {product.taxExempt && <Badge variant="success">Isento de IR</Badge>}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {product.description && (
                <p className="text-surface-300">{product.description}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-surface-800/50 rounded-lg text-center">
                  <p className="text-surface-400 text-sm mb-1">Rentabilidade</p>
                  <p className="text-xl font-bold text-primary-400">{formatRate(product)}</p>
                  <p className="text-xs text-surface-500">{rateTypeLabels[product.rateType]}</p>
                </div>
                <div className="p-4 bg-surface-800/50 rounded-lg text-center">
                  <p className="text-surface-400 text-sm mb-1">Aplicação Mínima</p>
                  <p className="text-xl font-bold text-surface-100">{formatCurrency(product.minimumAmount)}</p>
                </div>
                <div className="p-4 bg-surface-800/50 rounded-lg text-center">
                  <p className="text-surface-400 text-sm mb-1">Liquidez</p>
                  <p className="text-xl font-bold text-surface-100">D+{product.liquidityDays}</p>
                  <p className="text-xs text-surface-500">{liquidityLabels[product.liquidityType]}</p>
                </div>
                <div className="p-4 bg-surface-800/50 rounded-lg text-center">
                  <p className="text-surface-400 text-sm mb-1">Risco</p>
                  <p className={`text-xl font-bold ${risk.color}`}>{risk.label}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                {product.maturityDate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-surface-400" />
                    <span className="text-surface-400">Vencimento:</span>
                    <span className="text-surface-100">
                      {new Date(product.maturityDate).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
                {product.minimumTerm && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-surface-400" />
                    <span className="text-surface-400">Prazo Mínimo:</span>
                    <span className="text-surface-100">{product.minimumTerm} dias</span>
                  </div>
                )}
                {product.indexer && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-surface-400" />
                    <span className="text-surface-400">Indexador:</span>
                    <span className="text-surface-100">{product.indexer}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Simulator */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Simulador
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-surface-400 mb-2">Valor da Aplicação (R$)</label>
                  <Input
                    type="number"
                    placeholder={`Mínimo: ${formatCurrency(product.minimumAmount)}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-surface-400 mb-2">Prazo (dias)</label>
                  <select
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                  >
                    <option value="30">30 dias</option>
                    <option value="90">90 dias</option>
                    <option value="180">180 dias</option>
                    <option value="365">1 ano</option>
                    <option value="730">2 anos</option>
                    <option value="1095">3 anos</option>
                  </select>
                </div>
              </div>

              <Button
                variant="secondary"
                onClick={handleSimulate}
                disabled={!amount || simulationMutation.isPending}
              >
                {simulationMutation.isPending ? 'Calculando...' : 'Simular'}
              </Button>

              {simulationMutation.data && (
                <div className="mt-4 p-4 bg-surface-800/50 rounded-lg space-y-3">
                  <h4 className="font-medium text-surface-100">Resultado da Simulação</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-surface-400 text-sm">Rendimento Bruto</p>
                      <p className="text-lg font-bold text-surface-100">
                        {formatCurrency(simulationMutation.data.grossReturn)}
                      </p>
                    </div>
                    <div>
                      <p className="text-surface-400 text-sm">IR</p>
                      <p className="text-lg font-bold text-red-400">
                        -{formatCurrency(simulationMutation.data.taxAmount)}
                      </p>
                    </div>
                    {simulationMutation.data.iofAmount > 0 && (
                      <div>
                        <p className="text-surface-400 text-sm">IOF</p>
                        <p className="text-lg font-bold text-red-400">
                          -{formatCurrency(simulationMutation.data.iofAmount)}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-surface-400 text-sm">Rendimento Líquido</p>
                      <p className="text-lg font-bold text-green-400">
                        {formatCurrency(simulationMutation.data.netReturn)}
                      </p>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-surface-700 flex justify-between items-center">
                    <span className="text-surface-400">Taxa Efetiva</span>
                    <span className="text-primary-400 font-medium">
                      {(simulationMutation.data.effectiveRate * 100).toFixed(2)}% a.a.
                    </span>
                  </div>
                  {simulationMutation.data.savingsReturn > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-surface-400">Comparado à Poupança</span>
                      <span className="text-green-400 font-medium">
                        +{formatCurrency(simulationMutation.data.advantage)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Suitability Warning */}
          {suitability && !suitability.compatible && (
            <Card className="border-yellow-500/30">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-400">Atenção ao Perfil</p>
                    <p className="text-sm text-surface-300 mt-1">
                      {suitability.warning || 'Este produto pode não ser adequado ao seu perfil de investidor.'}
                    </p>
                    <p className="text-xs text-surface-400 mt-2">
                      Seu perfil: {suitability.customerProfile} | Risco do produto: {risk.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Apply Card */}
          <Card>
            <CardHeader>
              <CardTitle>Aplicar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!profile ? (
                <div className="text-center py-4">
                  <Shield className="w-8 h-8 text-surface-400 mx-auto mb-2" />
                  <p className="text-sm text-surface-400 mb-3">
                    Complete seu perfil de investidor para aplicar
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate('/banking/investments/profile')}
                  >
                    Completar Perfil
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm text-surface-400">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Perfil: {profile.profile}</span>
                  </div>
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => setShowApplyModal(true)}
                    disabled={!product.isAvailable}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    {product.isAvailable ? 'Aplicar Agora' : 'Indisponível'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5" />
                Informações Importantes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-surface-300">
              <p>• Rentabilidade passada não é garantia de rentabilidade futura.</p>
              <p>• Investimentos estão sujeitos a riscos, incluindo perda do capital investido.</p>
              {product.taxExempt ? (
                <p className="text-green-400">• Este produto é isento de Imposto de Renda para pessoa física.</p>
              ) : (
                <p>• IR segue tabela regressiva: 22,5% (até 180 dias) a 15% (acima de 720 dias).</p>
              )}
              {product.prospectusUrl && (
                <a
                  href={product.prospectusUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-400 hover:underline block"
                >
                  Ver Prospecto Completo →
                </a>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Confirmar Aplicação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-surface-400 mb-2">Valor da Aplicação (R$)</label>
                <Input
                  type="number"
                  placeholder={`Mínimo: ${formatCurrency(product.minimumAmount)}`}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                {Number(amount) * 100 < product.minimumAmount && amount && (
                  <p className="text-xs text-red-400 mt-1">
                    Valor mínimo: {formatCurrency(product.minimumAmount)}
                  </p>
                )}
              </div>

              <div className="p-3 bg-surface-800/50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-400">Produto</span>
                  <span className="text-surface-100">{product.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Rentabilidade</span>
                  <span className="text-primary-400">{formatRate(product)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Liquidez</span>
                  <span className="text-surface-100">D+{product.liquidityDays}</span>
                </div>
              </div>

              {suitability && !suitability.compatible && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm text-yellow-400">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    Este produto pode não ser adequado ao seu perfil. Deseja continuar?
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setShowApplyModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={handleApply}
                  disabled={
                    !amount ||
                    Number(amount) * 100 < product.minimumAmount ||
                    applyMutation.isPending
                  }
                >
                  {applyMutation.isPending ? 'Aplicando...' : 'Confirmar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
